import { MenuItem as MUIMenuItem, Paper } from '@mui/material';
import MuiPopper from '@mui/material/Popper';
import { useMemo } from 'react';

import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

interface VariableTypeaheadMenuProps {
  options: string[];
  selectedIndex: number | null;
  /** Called when the user clicks/selects an option. */
  onSelect: (variableName: string, index: number) => void;
  /** Called when the user hovers over an option. */
  onHighlight: (index: number) => void;
  /** Prefix for menu item IDs (for aria-activedescendant). */
  idPrefix: string;
  /**
   * When true, uses `onMouseDown` with `preventDefault` instead of `onClick`
   * to prevent the contenteditable from losing focus. The Lexical plugin does
   * not need this because LexicalTypeaheadMenuPlugin handles focus internally.
   */
  preventFocusLoss?: boolean;
  /**
   * Optional callback to provide additional MUI MenuItem props per item.
   * Used by the Lexical plugin to set ref callbacks for scroll-into-view.
   */
  getExtraMenuItemProps?: (
    index: number
  ) => React.ComponentProps<typeof MUIMenuItem>;
}

/**
 * Shared dropdown menu for variable typeahead, used by both the Lexical
 * VariableTypeaheadPlugin (rich text editor) and the title editor's
 * inline typeahead.
 *
 * Renders inside a LuminaryThemeProvider because the slide editor content
 * area may be inside a CustomerThemeProvider.
 */
export function VariableTypeaheadMenu({
  options,
  selectedIndex,
  onSelect,
  onHighlight,
  idPrefix,
  preventFocusLoss,
  getExtraMenuItemProps,
}: VariableTypeaheadMenuProps) {
  return (
    <LuminaryThemeProvider>
      <Paper
        elevation={8}
        sx={{
          maxHeight: 300,
          overflow: 'auto',
          border: `1px solid ${COLORS.GRAY[200]}`,
          py: '4px',
        }}
      >
        {options.map((name, index) => (
          <MenuItem
            key={name}
            label={name}
            muiMenuItemProps={{
              // Spread extra props first so our critical handlers
              // (onMouseDown for focus-loss prevention, sx) are never
              // silently overridden.
              ...getExtraMenuItemProps?.(index),
              id: `${idPrefix}-${index}`,
              role: 'option',
              'aria-selected': selectedIndex === index,
              selected: selectedIndex === index,
              onMouseEnter: () => onHighlight(index),
              ...(preventFocusLoss
                ? {
                    onMouseDown: (e: React.MouseEvent) => {
                      // Prevent the contenteditable from losing focus.
                      e.preventDefault();
                      onSelect(name, index);
                    },
                  }
                : {}),
              sx: {
                px: '14px',
                py: '10px',
              },
            }}
            onClick={
              preventFocusLoss
                ? // onClick is required by MenuItem but we handle selection
                  // via onMouseDown above to prevent focus loss.
                  () => {}
                : () => onSelect(name, index)
            }
          />
        ))}
      </Paper>
    </LuminaryThemeProvider>
  );
}

/**
 * Virtual anchor element that reads the live native selection for Popper
 * positioning. Works correctly inside CSS `transform:scale()` containers
 * because `getBoundingClientRect()` on a Range always returns viewport
 * coordinates.
 */
export function useVirtualSelectionAnchor() {
  return useMemo(
    () => ({
      getBoundingClientRect: () => {
        const nativeSelection = window.getSelection();
        if (nativeSelection && nativeSelection.rangeCount > 0) {
          return nativeSelection.getRangeAt(0).getBoundingClientRect();
        }
        return new DOMRect();
      },
    }),
    []
  );
}

interface VariableTypeaheadPopperProps extends VariableTypeaheadMenuProps {
  anchorEl: ReturnType<typeof useVirtualSelectionAnchor>;
}

/**
 * Complete Popper + Menu combo for variable typeahead. Convenience wrapper
 * that combines the virtual anchor positioning with the menu rendering.
 */
export function VariableTypeaheadPopper({
  anchorEl,
  ...menuProps
}: VariableTypeaheadPopperProps) {
  return (
    <MuiPopper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      data-slide-editor-variable-picker
      sx={{ zIndex: zIndexes.POPOVER }}
    >
      <VariableTypeaheadMenu {...menuProps} />
    </MuiPopper>
  );
}
